"use client";

import {
  LandingCheckoutGetCartSummaryCenterInvoiceOutputApiModel,
  LandingCheckoutGetCartSummaryOutputApiModel,
} from "@/Apis/ClinicApi";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import {
  Badge,
  Button,
  Divider,
  Empty,
  List,
  Popover,
  Skeleton,
  Space,
  Typography,
  message,
  theme,
} from "antd";

import { useLandingCheckout } from "@/Areas/Landing/Hooks";
import { captureCartReadScope, getCartReadScope, readCartSummary } from "@/Areas/Landing/Hooks/cartSummaryRequest";
import { Format, If, SmartLink } from "@/Components";
import { useResponsive, useT } from "@/Hooks";
import { Icon } from "@iconify/react";

const { Text } = Typography;

export interface HeaderCartButtonProps {
  className?: string;
  compact?: boolean;
}

function formatMoney(n?: number) {
  return (n ?? 0).toLocaleString("fa-IR");
}

function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

export const HeaderCartButton: FC<HeaderCartButtonProps> = ({
  className,
  compact = false,
}) => {
  const { token } = theme.useToken();
  const { isMobile } = useResponsive();
  const t = useT();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { cartSummaryDraw, setCartItemsCount } = useLandingCheckout();
  const [summary, setSummary] =
    useState<LandingCheckoutGetCartSummaryOutputApiModel>();
  const summaryVersion = useRef(0);
  const scope = getCartReadScope();

  const trigger: any = useMemo(
    () => (isTouchDevice() ? ["click"] : ["hover", "click"]),
    [],
  );

  const getSummary = async (silent?: boolean) => {
    const version = ++summaryVersion.current;
    const isCurrentScope = captureCartReadScope();
    const isCurrent = () => version === summaryVersion.current && isCurrentScope();
    try {
      if (!silent) { setLoading(true); setSummary(undefined); }
      const { Result } = await readCartSummary();
      if (isCurrent()) setSummary(Result);
    } catch {
      if (isCurrent()) message.error(
        t(
          "landing.headerCart.error.loadFailed",
          undefined,
          "Failed to load cart summary",
        ),
      );
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    getSummary();
    return () => { summaryVersion.current++; };
  }, [cartSummaryDraw, scope.authToken, scope.domain, scope.language]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await getSummary(true);
    setRefreshing(false);
  };

  // گروه بندی بر اساس شعبه
  const invoicesByBranch = useMemo(() => {
    if (!summary?.CenterInvoices) return {};

    return summary.CenterInvoices.reduce(
      (acc, inv) => {
        if (!acc[inv.CenterBranchId]) acc[inv.CenterBranchId] = [];
        acc[inv.CenterBranchId].push(inv);
        return acc;
      },
      {} as Record<
        number,
        LandingCheckoutGetCartSummaryCenterInvoiceOutputApiModel[]
      >,
    );
  }, [summary]);

  const totalCount = useMemo(() => {
    return (
      summary?.CenterInvoices?.reduce(
        (a, inv) =>
          a + inv.Items.reduce((x, it) => x + Number(it.Count ?? 0), 0),
        0,
      ) ?? 0
    );
  }, [summary]);

  const totalPayable = useMemo(() => {
    return (
      summary?.CenterInvoices?.reduce(
        (a, inv) => a + (inv.PayableAmount ?? 0),
        0,
      ) ?? 0
    );
  }, [summary]);

  useEffect(() => {
    setCartItemsCount(totalCount);
  }, [totalCount]);

  const content = (
    <div style={{ width: 420, maxWidth: "calc(100vw - 32px)" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Text strong>
          {t("landing.headerCart.title", undefined, "Shopping cart")}
        </Text>
        <Space size={8}>
          <Button
            type="text"
            size="small"
            icon={<Icon icon="solar:refresh-outline" />}
            loading={refreshing}
            onClick={handleRefresh}
          />
          <Text type="secondary">
            {totalCount
              ? t(
                  "landing.headerCart.count",
                  { count: totalCount },
                  "{count} items",
                )
              : t("landing.headerCart.emptyShort", undefined, "Empty")}
          </Text>
        </Space>
      </div>

      <Divider />

      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : !summary?.CenterInvoices?.length ? (
        <Empty
          description={t(
            "landing.headerCart.empty",
            undefined,
            "Your shopping cart is empty",
          )}
        />
      ) : (
        <div
          style={{
            maxHeight: 420,
            overflow: "auto",
            paddingInlineEnd: 6,
          }}
        >
          {Object.entries(invoicesByBranch).map(([branchId, invoices]) => {
            const branch = summary.CenterBranchs?.[Number(branchId)];

            return (
              <div key={branchId} style={{ marginBottom: 16 }}>
                <Text strong>
                  {branch?.Name ??
                    t("landing.headerCart.branchFallback", undefined, "Branch")}
                </Text>

                <Divider style={{ margin: "8px 0" }} />

                <List
                  dataSource={invoices}
                  split={false}
                  renderItem={(inv) => (
                    <List.Item
                      style={{
                        padding: 10,
                        borderRadius: 12,
                        background: token.colorFillQuaternary,
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>
                            {inv.Product?.Name ??
                              t(
                                "landing.headerCart.productFallback",
                                undefined,
                                "Product",
                              )}
                          </Text>
                          <Text strong>
                            <Format
                              type="Number"
                              value={inv.PayableAmount}
                              currency={branch?.Currency}
                            />
                          </Text>
                        </div>

                        <div style={{ marginTop: 6 }}>
                          {inv.Items.map((it) => {
                            const item = summary.CenterItems?.[it.CenterItemId];
                            return (
                              <div
                                key={it.Id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 12,
                                  paddingBlock: 2,
                                }}
                              >
                                <span>
                                  {item?.Name ??
                                    t(
                                      "landing.headerCart.itemFallback",
                                      undefined,
                                      "Item",
                                    )}
                                </span>
                                <span>
                                  {it.Count} × {formatMoney(it.Amount)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            );
          })}
        </div>
      )}
      <Divider />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Text type="secondary">
          {t(
            "landing.headerCart.totalPayable",
            undefined,
            "Total payable amount",
          )}
        </Text>
        <Text strong style={{ fontSize: 16 }}>
          {formatMoney(totalPayable)}
        </Text>
      </div>

      <SmartLink
        href="/Checkout/Cart"
        onClick={() => setOpen(false)}
        style={{ display: "block" }}
      >
        <Button
          type="primary"
          block
          size="large"
          disabled={!summary?.CenterInvoices?.length}
        >
          {t("landing.headerCart.checkout", undefined, "Complete order")}
        </Button>
      </SmartLink>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger={trigger}
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
    >
      <Badge count={totalCount} size="small">
        <Button
          type="default"
          icon={<Icon icon="solar:cart-large-2-outline" />}
          className={className}
          shape={isMobile || compact ? "circle" : "default"}
          size={compact ? "small" : "middle"}
        >
          <If condition={!isMobile && !compact}>
            {t("landing.headerCart.title", undefined, "Shopping cart")}
          </If>
          {totalPayable > 0 && !loading && !compact && (
            <Text type="secondary" style={{ marginInlineStart: 8 }}>
              {formatMoney(totalPayable)}
            </Text>
          )}
        </Button>
      </Badge>
    </Popover>
  );
};

export default HeaderCartButton;
